import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import Frame from './Frame';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import {
  AuthenticationResult,
  EventType,
  InteractionType,
} from '@azure/msal-browser';
import { msalInstance } from 'index';
import { getRuntimeAuthConfig } from 'core/auth/authConfig';
import { scheduleRoutePrefetchOnIdle } from 'core/prefetch';
import { ScrollToTop } from './initialization/ScrollToTop';
import { UnauthorizedComponent } from './initialization/UnauthorizedComponent';
import { ErrorComponent } from './initialization/ErrorComponent';
import { LoadingComponent } from './initialization/LoadingComponent';

const MainPage = lazy(() => import('views/MainPage'));
const JobDetailsPage = lazy(() => import('views/JobDetailsPage'));
const ScheduleFormPage = lazy(() => import('views/ScheduleFormPage'));
const NotFound = lazy(() => import('views/NotFoundPage'));

const App = () => {
  const { loginRequest } = getRuntimeAuthConfig();

  useEffect(() => {
    const callbackId = msalInstance.addEventCallback(event => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
      }
    });
    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  useEffect(() => {
    return scheduleRoutePrefetchOnIdle();
  }, []);

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      errorComponent={ErrorComponent}
      loadingComponent={LoadingComponent}
    >
      <Frame>
        <ScrollToTop />
        <Suspense fallback={<LoadingComponent />}>
          <Routes>
            <Route path="/scheduleCreate" element={<ScheduleFormPage />} />
            <Route
              path="/schedules/duplicate/:scheduleId"
              element={<ScheduleFormPage />}
            />
            <Route
              path="/editSchedule/:scheduleId"
              element={<ScheduleFormPage />}
            />
            <Route path="/schedules" element={<MainPage />} />
            <Route path="/schedules/:scheduleId" element={<JobDetailsPage />} />
            <Route path="/unauthorized" element={<UnauthorizedComponent />} />
            <Route path="" element={<MainPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Frame>
    </MsalAuthenticationTemplate>
  );
};

export default App;
